import { Flex, Icon, Text } from '@chakra-ui/react'
import React from 'react'
import IconSearchContacts from '~/assets/svgs/icon-search-contacts.svg?react'
import IconXMagnifyingGlass from '~/assets/svgs/icon-x-magnifying-glass.svg?react'
import { LoadingSpinner } from '~/components/shared/LoadingSpinner'
import { ScrollingList } from '~/components/shared/ScrollingList'
import {
  ContactDetails,
  ContactNumber,
} from '~/pages/Contacts/viewModels/Contacts.viewmodel'
import { SearchValue } from '~/types/Shared'

import { ContactSourceType } from '@mangovoice/types'

import { useContactCardsListStyles } from './componentStyles/ContactCardsList.style'
import { ContactCard } from './ContactCard'
import { isPhoneNumber } from './utils/stringUtils'

interface ContactCardsListProps {
  isSearching?: boolean
  searchValue?: SearchValue
  searchContacts?: ContactDetails[]
  onSearchEndReached?: () => void
  isSearchLoading?: boolean
  onStartCall: (
    callee:
      | string
      | {
          contactNumber: ContactNumber
          contact: ContactDetails
        },
  ) => void
}
export const ContactCardsList = ({
  isSearching,
  searchValue,
  searchContacts,
  onSearchEndReached,
  isSearchLoading,
  onStartCall: handleStartCall,
}: ContactCardsListProps) => {
  const styles = useContactCardsListStyles()
  const getContactCardsListContent = () => {
    if (!isSearching) {
      return (
        <Flex sx={styles.emptySearchBody}>
          <Icon as={IconSearchContacts} sx={styles.emptySearchIcon} />
          <Flex sx={styles.emptySearchTextContainer}>
            <Text sx={styles.emptySearchText}>
              Enter a name or number above to start a search
            </Text>
          </Flex>
        </Flex>
      )
    }
    if (!searchContacts || (!isSearchLoading && searchContacts.length === 0)) {
      const shouldShowContactCard =
        searchValue && isPhoneNumber(searchValue.value)
      return (
        <>
          <Flex sx={styles.resultsHeader}>
            <Text sx={styles.resultsHeaderText}>Search Results</Text>
            <Text sx={styles.emptySearchResultsText}>
              We didn't find any contacts matching{' '}
              <Text as="span">"{searchValue?.value}"</Text>
            </Text>
          </Flex>
          {shouldShowContactCard && (
            <ContactCard
              contact={{
                id: -1,
                name: '',
                defaultNumber: {
                  id: 0,
                  number: searchValue.value,
                  label: 'other',
                  isPrimary: false,
                  isExtension: false,
                  availableActions: [],
                  availableQuickActions: [],
                },
                numbers: [],
                source: ContactSourceType.Unknown,
                sourceName: 'Failed Search',
              }}
              onStartCall={handleStartCall}
            />
          )}
          {!shouldShowContactCard && (
            <Flex sx={styles.emptySearchResultsBody}>
              <Icon
                as={IconXMagnifyingGlass}
                sx={styles.emptySearchResultsIcon}
              />
            </Flex>
          )}
        </>
      )
    } else if (isSearchLoading && searchContacts.length === 0) {
      return (
        <>
          <Flex sx={styles.resultsHeader}>
            <Text sx={styles.resultsHeaderText}>Search Results</Text>
          </Flex>
          <Flex sx={styles.searchResultsContainer}>
            <LoadingSpinner />
          </Flex>
        </>
      )
    } else {
      return (
        <>
          <Flex sx={styles.resultsHeader}>
            <Text sx={styles.resultsHeaderText}>Search Results</Text>
          </Flex>
          <ScrollingList
            display="flex"
            width="full"
            height="full"
            minHeight="0"
            onEndReached={onSearchEndReached}
            showLoaderFooter={isSearchLoading}
            scrollbarOptions={{ showOnHoverOnly: true }}
            itemData={searchContacts}
            onBuildItemContent={(_, contact) => {
              return (
                <ContactCard
                  key={contact.id}
                  contact={contact}
                  onStartCall={handleStartCall}
                />
              )
            }}
          />
        </>
      )
    }
  }

  return (
    <Flex
      flexDirection="column"
      sx={{ height: 'calc(100vh - 400px)', overflow: 'scroll' }}
    >
      {getContactCardsListContent()}
    </Flex>
  )
}
